import { useMemo } from 'react';
import { NavLink, generatePath } from 'react-router-dom';
import isEqual from 'lodash.isequal';
import {
  useQueryState,
  IconTrash,
  ButtonSecondary,
  PermissionEnum,
  Currency,
  formatBalance,
} from '@ui/src';
import { useAppDispatch, useAppSelector } from '@store';
import { selectFees } from '../../hoc/tariffs/store/slices/tariff-page';
import Card from '../partial/card/card';
import FeesTable from './fees-table';
import { useDeleteTariffFeeMutation } from '../../hoc/tariffs/store/services';
import { TariffItemFee, FeeAmountTypeEnum } from '../../hoc/tariffs/types';
import { feesSchema } from '../../hoc/tariffs/variables/fees';
import { FeesTypes } from './types';

import { TariffsRoutes } from '../../routes';

import cn from './tariffs.module.css';

const formatFeesList = (
  list: TariffItemFee[],
  tariffId?: string,
  baseCurrency?: Currency,
  currenciesList?: Currency[],
): TariffItemFee[] => {
  const nextList = list?.map((item) => {
    const result = { ...item };

    result.url = generatePath(TariffsRoutes.TARIFFS_INFO_FEE, {
      tariffId: tariffId as string,
      feeId: result.id,
    });

    const currentCurrency = result?.feeCurrencyId
      ? currenciesList?.find(
          (currency) => currency.id === result?.feeCurrencyId,
        )
      : baseCurrency;

    const currentCurrencyName = currentCurrency?.name;

    const render: Record<string, React.JSX.Element> = {
      writeOffValue: (
        <div className={cn.feeWriteOffValue}>
          {/* eslint-disable-next-line no-nested-ternary */}
          {item.feeAmountType === FeeAmountTypeEnum.PERCENT
            ? `${item.feePercentAmount}%`
            : item.feeFixedAmount
              ? `${formatBalance(
                  item.feeFixedAmount,
                  currentCurrency,
                )} ${currentCurrencyName}`
              : '-'}
          {item.feePercentMinAmount?.toString() &&
            item.feePercentMaxAmount?.toString() && (
              <div className={cn.feeWriteOffValueInfo}>
                <span>{`min: ${formatBalance(
                  item.feePercentMinAmount,
                  baseCurrency,
                )} ${baseCurrency?.name}`}</span>
                {item.feePercentMaxAmount && (
                  <span>{`max: ${formatBalance(
                    item.feePercentMaxAmount,
                    baseCurrency,
                  )} ${baseCurrency?.name}`}</span>
                )}
              </div>
            )}
        </div>
      ),
      feeAmountType: (
        <div>
          {item.feeAmountType === FeeAmountTypeEnum.PERCENT
            ? 'Percent of balance'
            : 'Fixed value'}
        </div>
      ),
    };

    result.render = render;

    return result;
  });

  return nextList;
};

const Fees = ({ tariffId, fees, isLoading }: FeesTypes) => {
  const { selectedFeesList } = useAppSelector(
    (state) => state.tariffs.tariffPage,
  );

  const { activePermissions, baseCurrency, currenciesList } = useAppSelector(
    (state) => state.configurationDictionaries,
  );

  const dispatch = useAppDispatch();

  const { sort } = useQueryState(feesSchema);

  const formattedData = useMemo(() => {
    const data = formatFeesList(
      fees as TariffItemFee[],
      tariffId,
      baseCurrency,
      currenciesList,
    );

    return data;
  }, [fees, sort, baseCurrency, currenciesList]);

  const [deleteFee, { isLoading: removeFeesInProgress }] =
    useDeleteTariffFeeMutation();

  const deleteTariffFees = () => {
    selectedFeesList.forEach((item) => {
      const currentItem = fees?.find((fee) => fee.id === item);

      if (currentItem) {
        deleteFee({
          id: tariffId as string,
          feeId: item,
        });
      }
    });
  };

  const onRowsSelect = (rows: string[]) => {
    if (!isEqual(selectedFeesList, rows) && (fees?.length as number) > 0) {
      dispatch(selectFees(rows));
    }
  };

  return (
    <Card
      className={`${cn.tariffCard} ${cn.tariffFeesListCard} ${
        !fees?.length ? cn.emptyFeesTable : ''
      }`}
      isLoading={isLoading}
      title={
        <div className={cn.infoTitleWrapper}>
          <div className={cn.infoTitle}>Fees</div>
          {activePermissions?.includes(PermissionEnum.FEES_CREATE_ACTION) && (
            <NavLink
              to={generatePath(TariffsRoutes.TARIFFS_INFO_ADD_FEE, {
                tariffId: tariffId as string,
              })}
              className={cn.infoTitleLink}
            >
              <span className={cn.infoTitleLinkIcon}>+</span>
              <span className={cn.infoTitleLinkText}>Add fee</span>
            </NavLink>
          )}

          {selectedFeesList.length > 0 && (
            <>
              <div className={cn.separator} />
              <ButtonSecondary
                className={`${cn.infoTitleButton} button-white`}
                text={
                  removeFeesInProgress
                    ? 'Loading...'
                    : `Remove fee${selectedFeesList.length > 1 ? 's' : ''}`
                }
                icon={<IconTrash />}
                onClick={() => deleteTariffFees()}
              />
            </>
          )}
        </div>
      }
    >
      <FeesTable
        data={formattedData}
        onRowsSelect={
          activePermissions?.includes(PermissionEnum.FEES_EDIT_ACTION)
            ? onRowsSelect
            : undefined
        }
      />
    </Card>
  );
};

export default Fees;
